import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Grid, Card, CardContent, Table,
  TableHead, TableRow, TableCell, TableBody, Button, TextField, CircularProgress,
  Alert, Chip, Divider, TableContainer, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  TrendingUp, Receipt, ShoppingCart, BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  getDailySalesReport, getSalesRange, getTopSellingProducts,
  getCashierPerformance, getRevenueSummary,
} from '../../api/api';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const today = new Date().toISOString().slice(0, 10);

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function KpiCard({ label, value, icon, color = '#667eea', sub }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: `2px solid ${color}22` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color }}>
          {icon}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
        </Box>
        <Typography variant="h5" fontWeight={900} color={color}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function SimpleBar({ data, maxValue, labelKey = 'hour', valueKey = 'revenue' }) {
  if (!data?.length) return <Alert severity="info">No data available.</Alert>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {data.map((row, i) => {
        const pct = maxValue > 0 ? ((row[valueKey] || 0) / maxValue) * 100 : 0;
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 50, color: '#64748b', fontWeight: 600 }}>{row[labelKey]}</Typography>
            <Box sx={{ flex: 1, bgcolor: '#f1f5f9', borderRadius: 1, height: 18, overflow: 'hidden' }}>
              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: '#667eea', borderRadius: 1, minWidth: pct > 0 ? 4 : 0, transition: 'width 0.4s' }} />
            </Box>
            <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{KES(row[valueKey])}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function SalesReports() {
  const [tab, setTab] = useState(0);

  /* Daily */
  const [dailyDate, setDailyDate] = useState(today);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');

  /* Range */
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10);
  });
  const [rangeEnd, setRangeEnd] = useState(today);
  const [rangeData, setRangeData] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState('');

  /* Top Products */
  const [topStart, setTopStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [topEnd, setTopEnd] = useState(today);
  const [topData, setTopData] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState('');

  /* Cashier */
  const [cashierStart, setCashierStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [cashierEnd, setCashierEnd] = useState(today);
  const [cashierData, setCashierData] = useState([]);
  const [cashierLoading, setCashierLoading] = useState(false);
  const [cashierError, setCashierError] = useState('');

  /* Revenue Summary */
  const [period, setPeriod] = useState('week');
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  /* load on tab change */
  useEffect(() => {
    if (tab === 0) loadDaily();
    if (tab === 1) loadRange();
    if (tab === 2) loadTop();
    if (tab === 3) loadCashier();
    if (tab === 4) loadSummary();
  }, [tab]);

  /* ── loaders ── */
  const loadDaily = async () => {
    try { setDailyLoading(true); setDailyError(''); const r = await getDailySalesReport(dailyDate); setDailyData(r.data); }
    catch (e) { setDailyError(e.response?.data?.message || 'Failed to load daily report.'); setDailyData(null); }
    finally { setDailyLoading(false); }
  };

  const loadRange = async () => {
    try { setRangeLoading(true); setRangeError(''); const r = await getSalesRange({ startDate: rangeStart, endDate: rangeEnd }); setRangeData(r.data?.data || r.data || []); }
    catch (e) { setRangeError(e.response?.data?.message || 'Failed to load range report.'); setRangeData([]); }
    finally { setRangeLoading(false); }
  };

  const loadTop = async () => {
    try { setTopLoading(true); setTopError(''); const r = await getTopSellingProducts({ startDate: topStart, endDate: topEnd }); setTopData(r.data?.products || r.data || []); }
    catch (e) { setTopError(e.response?.data?.message || 'Failed to load top products.'); setTopData([]); }
    finally { setTopLoading(false); }
  };

  const loadCashier = async () => {
    try { setCashierLoading(true); setCashierError(''); const r = await getCashierPerformance({ startDate: cashierStart, endDate: cashierEnd }); setCashierData(r.data?.cashiers || r.data || []); }
    catch (e) { setCashierError(e.response?.data?.message || 'Failed to load cashier data.'); setCashierData([]); }
    finally { setCashierLoading(false); }
  };

  const loadSummary = async () => {
    try { setSummaryLoading(true); setSummaryError(''); const r = await getRevenueSummary(period); setSummaryData(r.data?.summary || r.data || null); }
    catch (e) { setSummaryError(e.response?.data?.message || 'Failed to load summary.'); setSummaryData(null); }
    finally { setSummaryLoading(false); }
  };

  const maxHourly = dailyData?.hourly?.reduce((m, r) => Math.max(m, r.revenue || 0), 0) || 0;
  const totalTopRevenue = topData.reduce((s, p) => s + (p.revenue || 0), 0);

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={800} color="#1e293b" gutterBottom>Sales Reports</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Comprehensive analytics for sales, products, and cashier performance</Typography>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #e2e8f0', px: 2, pt: 1 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Daily" />
          <Tab label="Range" />
          <Tab label="Top Products" />
          <Tab label="Cashier Performance" />
          <Tab label="Revenue Summary" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ─── DAILY ─── */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField type="date" label="Date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <Button variant="contained" onClick={loadDaily} sx={{ borderRadius: 2 }}>Load</Button>
            </Box>
            {dailyLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : dailyError ? <Alert severity="error">{dailyError}</Alert> : !dailyData ? <Alert severity="info">Select a date and click Load.</Alert> : (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}><KpiCard label="Total Revenue" value={KES(dailyData.totalRevenue || dailyData.revenue)} icon={<TrendingUp />} color="#22c55e" /></Grid>
                  <Grid item xs={6} sm={3}><KpiCard label="Transactions" value={dailyData.totalTransactions ?? dailyData.transactions ?? 0} icon={<Receipt />} color="#667eea" /></Grid>
                  <Grid item xs={6} sm={3}><KpiCard label="Avg Order Value" value={KES(dailyData.avgOrderValue || dailyData.averageOrderValue)} icon={<ShoppingCart />} color="#f59e0b" /></Grid>
                  <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
                      <CardContent>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">POS vs Online</Typography>
                        <Typography fontWeight={700}>{KES(dailyData.posSales || dailyData.posRevenue || 0)}</Typography>
                        <Typography variant="caption" color="text.secondary">POS | {KES(dailyData.onlineSales || dailyData.onlineRevenue || 0)} Online</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {dailyData.hourly?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} mb={2}>Hourly Breakdown</Typography>
                    <SimpleBar data={dailyData.hourly} maxValue={maxHourly} labelKey="hour" valueKey="revenue" />
                    <Divider sx={{ my: 3 }} />
                  </>
                )}

                {dailyData.topItems?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} mb={2}>Top Items Today</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                          {['Product', 'Qty', 'Revenue'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                        </TableRow></TableHead>
                        <TableBody>
                          {dailyData.topItems.map((item, i) => (
                            <TableRow key={i} hover>
                              <TableCell>{item.productName || item.name}</TableCell>
                              <TableCell>{item.quantity || item.qty}</TableCell>
                              <TableCell>{KES(item.revenue)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </TabPanel>

          {/* ─── RANGE ─── */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField type="date" label="From" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="To" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <Button variant="contained" onClick={loadRange} sx={{ borderRadius: 2 }}>Load</Button>
            </Box>
            {rangeLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : rangeError ? <Alert severity="error">{rangeError}</Alert> : rangeData.length === 0 ? <Alert severity="info">No data for the selected range.</Alert> : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Date', 'POS Revenue', 'Online Revenue', 'Total', 'Transactions'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {rangeData.map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{row.date || row.day}</TableCell>
                        <TableCell>{KES(row.posRevenue || row.pos || 0)}</TableCell>
                        <TableCell>{KES(row.onlineRevenue || row.online || 0)}</TableCell>
                        <TableCell><Typography fontWeight={700}>{KES(row.total || row.totalRevenue || 0)}</Typography></TableCell>
                        <TableCell>{row.transactions ?? row.count ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ─── TOP PRODUCTS ─── */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField type="date" label="From" value={topStart} onChange={(e) => setTopStart(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="To" value={topEnd} onChange={(e) => setTopEnd(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <Button variant="contained" onClick={loadTop} sx={{ borderRadius: 2 }}>Load</Button>
            </Box>
            {topLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : topError ? <Alert severity="error">{topError}</Alert> : topData.length === 0 ? <Alert severity="info">No data for the selected range.</Alert> : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Rank', 'Product', 'Qty Sold', 'Revenue', '% of Total'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {topData.map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Chip label={`#${i + 1}`} size="small" color={i === 0 ? 'warning' : 'default'} sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>{row.productName || row.name}</TableCell>
                        <TableCell>{row.quantitySold ?? row.quantity ?? 0}</TableCell>
                        <TableCell><Typography fontWeight={700}>{KES(row.revenue)}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ flex: 1, bgcolor: '#f1f5f9', borderRadius: 1, height: 8 }}>
                              <Box sx={{ width: `${totalTopRevenue > 0 ? ((row.revenue || 0) / totalTopRevenue) * 100 : 0}%`, height: '100%', bgcolor: '#667eea', borderRadius: 1 }} />
                            </Box>
                            <Typography variant="caption" fontWeight={700}>
                              {totalTopRevenue > 0 ? ((row.revenue / totalTopRevenue) * 100).toFixed(1) : 0}%
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ─── CASHIER PERFORMANCE ─── */}
          <TabPanel value={tab} index={3}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField type="date" label="From" value={cashierStart} onChange={(e) => setCashierStart(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="To" value={cashierEnd} onChange={(e) => setCashierEnd(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <Button variant="contained" onClick={loadCashier} sx={{ borderRadius: 2 }}>Load</Button>
            </Box>
            {cashierLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : cashierError ? <Alert severity="error">{cashierError}</Alert> : cashierData.length === 0 ? <Alert severity="info">No cashier data for the selected range.</Alert> : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: '#f8fafc' }}>
                    {['Cashier', 'Total Sales', 'Transactions', 'Avg Sale'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {cashierData.map((row, i) => (
                      <TableRow key={i} hover>
                        <TableCell fontWeight={600}>{row.cashierName || row.name}</TableCell>
                        <TableCell><Typography fontWeight={700} color="primary">{KES(row.totalSales || row.revenue)}</Typography></TableCell>
                        <TableCell>{row.transactionCount ?? row.transactions ?? 0}</TableCell>
                        <TableCell>{KES(row.avgSale || row.averageSale || ((row.totalSales || 0) / Math.max(1, row.transactionCount || 1)))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ─── REVENUE SUMMARY ─── */}
          <TabPanel value={tab} index={4}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Period</InputLabel>
                <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value)}>
                  <MenuItem value="day">Today</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="year">This Year</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={loadSummary} sx={{ borderRadius: 2 }}>Load</Button>
            </Box>
            {summaryLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : summaryError ? <Alert severity="error">{summaryError}</Alert> : !summaryData ? <Alert severity="info">Select a period and click Load.</Alert> : (
              <Grid container spacing={2}>
                {[
                  { label: 'Total Revenue', value: KES(summaryData.totalRevenue || summaryData.revenue), color: '#22c55e', icon: <TrendingUp /> },
                  { label: 'POS Revenue', value: KES(summaryData.posRevenue || summaryData.pos || 0), color: '#667eea', icon: <BarChartIcon /> },
                  { label: 'Online Revenue', value: KES(summaryData.onlineRevenue || summaryData.online || 0), color: '#f59e0b', icon: <ShoppingCart /> },
                  {
                    label: 'Growth',
                    value: `${summaryData.growthPercent != null ? (summaryData.growthPercent >= 0 ? '+' : '') + summaryData.growthPercent.toFixed(1) + '%' : '—'}`,
                    color: (summaryData.growthPercent ?? 0) >= 0 ? '#22c55e' : '#ef4444',
                    icon: <TrendingUp />,
                    sub: 'vs previous period',
                  },
                ].map((item) => (
                  <Grid item xs={6} sm={3} key={item.label}>
                    <KpiCard {...item} />
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
